import React, { ReactNode } from 'react';
import { Calendar, Target, Wrench } from 'lucide-react';
import BaseTrajectoryCard, { 
  BaseTrajectoryProps, 
  ThemeVocabulary, 
  TrajectoryCardRenderer 
} from './BaseTrajectoryCard';

// LEGO-specific trajectory card implementation
class LegoTrajectoryCardImplementation extends BaseTrajectoryCard {
  
  getThemeVocabulary(): ThemeVocabulary {
    return {
      unit: 'bricks',
      pace: 'bricks/day',
      goal: 'Nintendo Switch LEGO Set',
      completion: 'built',
      current: 'Building Rate',
      remaining: 'Bricks Needed'
    };
  }
  
  getContainerClassName(): string {
    return "bg-gradient-to-br from-red-100 via-yellow-100 to-blue-100 rounded-2xl p-6 border-4 border-slate-300 shadow-lg";
  }
  
  renderHeader(): ReactNode {
    const status = this.determineStatus();
    const statusConfig = {
      excellent: { bg: 'bg-green-200', text: 'text-green-800', border: 'border-green-400', label: '🏆 Master Builder!' },
      good: { bg: 'bg-blue-200', text: 'text-blue-800', border: 'border-blue-400', label: '⚡ Keep Building!' },
      monitor: { bg: 'bg-yellow-200', text: 'text-yellow-800', border: 'border-yellow-400', label: '⚡ Keep Building!' },
      speedup: { bg: 'bg-red-200', text: 'text-red-800', border: 'border-red-400', label: '🔨 Speed Up!' }
    };
    
    const config = statusConfig[status];
    
    return (
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="text-2xl animate-bounce">👷‍♂️</div>
          <div>
            <h3 className="text-xl font-bold text-slate-800">Master Builder Construction Timeline</h3>
            <p className="text-slate-600 text-sm">Nintendo Switch LEGO Set Progress</p>
          </div>
        </div>
        <div className={`px-3 py-2 rounded-full text-sm font-bold border-2 ${config.bg} ${config.text} ${config.border}`}>
          {config.label}
        </div>
      </div>
    );
  }
  
  renderPredictionSection(): ReactNode {
    const projectedDate = this.calculateProjectedDate();
    const remaining = this.calculateRemaining();
    const vocabulary = this.getThemeVocabulary();
    
    return (
      <div className="bg-white/80 rounded-xl p-5 mb-6 border-2 border-blue-400 relative overflow-hidden">
        <div className="absolute top-0 right-0 text-6xl opacity-10">🎮</div>
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-5 h-5 text-blue-600" />
          <span className="text-blue-600 font-semibold">
            Building at {this.props.dailyAverage.toFixed(1)} {vocabulary.unit} per day...
          </span>
        </div>
        <div className="text-2xl font-bold text-slate-800 mb-2">
          🎮 {vocabulary.goal} Complete in {this.formatTimeRemaining(projectedDate)}!
        </div>
        <div className="text-slate-600">
          Just {remaining} more {vocabulary.unit} to go! Keep up the awesome building! 🧱
        </div>
      </div>
    );
  }
  
  renderProgressVisualization(): ReactNode {
    const progressPercentage = this.calculateProgressPercentage();
    
    // LEGO instruction manual steps based on progress
    const getConstructionSteps = () => {
      const steps = [
        { name: "Foundation Base", range: [0, 100], icon: "🟫", status: progressPercentage > 20 ? "complete" : progressPercentage > 10 ? "building" : "planned" },
        { name: "Controller Frame", range: [100, 200], icon: "🟦", status: progressPercentage > 40 ? "complete" : progressPercentage > 20 ? "building" : "planned" },
        { name: "Screen Assembly", range: [200, 300], icon: "⬛", status: progressPercentage > 60 ? "complete" : progressPercentage > 40 ? "building" : "planned" },
        { name: "Joy-Con Attachment", range: [300, 400], icon: "🟨", status: progressPercentage > 80 ? "complete" : progressPercentage > 60 ? "building" : "planned" },
        { name: "Final Details", range: [400, 500], icon: "🟩", status: progressPercentage > 95 ? "complete" : progressPercentage > 80 ? "building" : "planned" }
      ];
      
      return steps;
    };
    
    const constructionSteps = getConstructionSteps();
    
    return (
      <div className="bg-white/80 rounded-xl p-5 mb-6 border-2 border-slate-300">
        <div className="flex items-center gap-2 mb-4">
          <Wrench className="w-5 h-5 text-slate-600" />
          <h4 className="font-bold text-slate-800">LEGO Instruction Manual - Build Progress</h4>
        </div>
        
        <div className="space-y-3">
          {constructionSteps.map((step, index) => (
            <div key={index} className="flex items-center gap-4 p-3 rounded-lg bg-slate-50">
              <div className="flex items-center gap-2">
                <span className="text-lg">{step.icon}</span>
                <span className="font-mono text-sm text-slate-500">Step {index + 1}</span>
              </div>
              
              <div className="flex-grow">
                <div className="font-semibold text-slate-800">{step.name}</div>
                <div className="text-xs text-slate-500">
                  {step.range[0]} - {step.range[1]} bricks
                </div>
              </div>
              
              <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                step.status === 'complete' ? 'bg-green-200 text-green-800' :
                step.status === 'building' ? 'bg-yellow-200 text-yellow-800' :
                'bg-gray-200 text-gray-600'
              }`}>
                {step.status === 'complete' ? '✅ Built' :
                 step.status === 'building' ? '🔨 Building' :
                 '📋 Planned'}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  renderMetrics(): ReactNode {
    const requiredPace = this.calculateRequiredDailyPace();
    const vocabulary = this.getThemeVocabulary();
    const statusMessage = this.getStatusMessage();
    const status = this.determineStatus();

    return (
      <div className="bg-blue-600 text-white rounded-lg p-4 mb-6 border-2 border-white relative overflow-hidden shadow-lg">
        <div className="absolute top-2 right-2 text-2xl opacity-50">🎯</div>
        <h4 className="font-bold text-lg text-blue-100">Target Pace</h4>
        <div className="text-center my-2">
          <div className="text-4xl font-bold">{requiredPace.toFixed(1)}</div>
          <div className="text-sm font-semibold opacity-90">{vocabulary.pace}</div>
        </div>
        
        {(status === 'monitor' || status === 'speedup') && statusMessage.suggestions.length > 0 && (
          <div className="border-t border-blue-400 mt-4 pt-3">
            <div className="text-xs text-blue-200 mb-2 text-center">🔧 Quick building boost ideas:</div>
            <div className="flex flex-wrap gap-2 justify-center">
              {statusMessage.suggestions.map((suggestion, i) => (
                <span key={i} className="text-xs bg-white text-blue-800 px-2 py-1 rounded-full">
                  {suggestion}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
}

// React component that creates and uses the LEGO implementation
export const LegoTrajectoryCard: React.FC<BaseTrajectoryProps> = (props) => {
  const implementation = new LegoTrajectoryCardImplementation(props);
  return <TrajectoryCardRenderer {...props} implementation={implementation} />;
};